import React, { useState } from "react";
import callIcon from "../assets/call.webp";
import chatIcon from "../assets/chat.webp";
import emailIcon from "../assets/mail.png";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telephone: "",
    comment: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.comment.trim()) {
      setSubmitMessage({
        type: 'error',
        text: 'Please enter your message before submitting.'
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const mutation = `
        mutation {
          contactUs(
            input: {
              name: ${JSON.stringify(formData.name)},
              email: ${JSON.stringify(formData.email)},
              telephone: ${JSON.stringify(formData.telephone)},
              comment: ${JSON.stringify(formData.comment)},
              company: ""
            }
          ) {
            status
          }
        }
      `;

      const response = await fetch('https://live-magento.ipromo.com/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: mutation,
          variables: {},
        }),
      });

      const result = await response.json();

      if (response.ok && !result.errors) {
        setSubmitMessage({
          type: 'success',
          text: 'Thank you for contacting us! We will get back to you soon.',
        });
        setFormData({ name: '', email: '', telephone: '', comment: '' });
      } else {
        throw new Error(result.errors?.[0]?.message || 'Failed to send message');
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      setSubmitMessage({
        type: "error",
        text: "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #ccc",
    fontSize: "13px",
    height: "38px",
    padding: "10px",
    width: "100%",
    fontFamily: "Montserrat",
    color: "#777",
  };

  const textareaStyle = {
    ...inputStyle,
    height: "205px",
    resize: "vertical" as const,
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "500",
    color: "#777",
    marginBottom: "8px",
    display: "block",
  };

  return (
    <div className="flex flex-col">
      <main className="flex-grow">
        {/* Contact Form Section */}
        <div className="w-full pt-[55px] pb-[86px] bg-white">
          <div className="px-5 max-w-[1710px] mx-auto">
            <div className="flex flex-wrap w-full">
              {/* Form Section - 2/3 width */}
              <div className="lg:max-w-[66.6667%] max-w-full w-full">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h1 className="text-3xl md:text-[22px] font-medium text-[#303030] leading-[26px] mb-5">
                    How Can We Help?
                  </h1>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-[40px]">
                    {/* Left Column - Form Fields */}
                    <div className="space-y-[18px]">
                      <div>
                        <label style={labelStyle}>
                          Name <span className="text-[#e02b27]">*</span>
                        </label>
                        <div className="control">
                          <input
                            name="name"
                            id="name"
                            title="Name"
                            value={formData.name}
                            className="input-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                            type="text"
                            data-validate="{required:true}"
                            aria-required="true"
                            onChange={handleInputChange}
                            required
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={labelStyle}>
                          Email <span className="text-[#e02b27]">*</span>
                        </label>
                        <div className="control">
                          <input
                            name="email"
                            id="email"
                            title="Email"
                            value={formData.email}
                            className="input-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                            type="email"
                            data-validate="{required:true}"
                            aria-required="true"
                            onChange={handleInputChange}
                            required
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={labelStyle}>Phone Number</label>
                        <div className="control">
                          <input
                            name="telephone"
                            id="telephone"
                            title="Phone Number"
                            value={formData.telephone}
                            className="input-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                            type="tel"
                            onChange={handleInputChange}
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Message */}
                    <div>
                      <label style={labelStyle}>
                        Message <span className="text-[#e02b27]">*</span>
                      </label>
                      <div className="control">
                        <textarea
                          name="comment"
                          id="comment"
                          title="Message"
                          value={formData.comment}
                          className="input-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onChange={handleInputChange}
                          required
                          style={textareaStyle}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="!mt-[72px] w-full md:!w-fit px-4 text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    style={{
                      backgroundColor: "#BB372E",
                      height: "38px",
                      fontSize: "16px",
                    }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                  {submitMessage && (
                    <div
                      className={`p-3 rounded-md ${
                        submitMessage.type === "success"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {submitMessage.text}
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Information - 1/3 width */}
              <div className="lg:max-w-[33.33333%] max-w-full w-full mt-[30px] md:mt-0 md:pl-10">
                <h2 className="text-3xl md:text-[22px] font-medium text-[#303030] leading-[26px] mb-10">
                  Contact Us
                </h2>

                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-center space-x-4">
                    <img src={callIcon} alt="Phone" className="w-8 h-8" />
                    <div>
                      <a
                        href="tel:877-927-3691"
                        className="text-[17px] leading-[21px] font-medium text-[#505050] hover:text-[#505050]"
                      >
                        877-927-3691
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-center space-x-4">
                    <img src={emailIcon} alt="Email" className="w-8 h-8" />
                    <div>
                      <a
                        href="mailto:hello@ppe.us"
                        className="text-[17px] leading-[21px] font-medium text-[#505050] hover:text-[#505050]"
                      >
                        hello@ppe.us
                      </a>
                    </div>
                  </div>

                  {/* Chat */}
                  <div className="flex items-center space-x-4">
                    <img src={chatIcon} alt="Chat" className="w-8 h-8" />
                    <div>
                      <span className="text-[17px] leading-[21px] font-medium text-[#505050] hover:text-[#505050]">
                        Chat with us
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
